import Api from '@/util/Api';

/**
 * ROBO-854. The FAA airspace at a shoot location (ROBO-834/853), with `noFly` worked out by the API's placement rule — the
 * same one `POST orders` refuses drone products on. Signed-in callers need no checkout pass; a guest would (ROBO-476).
 */
export const DRONE_CEILING_PATH = 'checkout/drone-ceiling';

export interface DroneLocation {
    latitude: number;
    longitude: number;
}

/** One FAA restriction at the point (`restrictions[]`). */
export interface DroneRestriction {
    kind: string;
    name: string;
    effect: 'prohibited' | 'authorization-required' | 'advisory' | string;
    detail?: string;
    notamId?: string;
    /** TFRs only, UTC. */
    effective?: string;
    expires?: string;
    /** ROBO-854: this restriction blocks drone work on the date asked about. */
    noFly?: boolean;
}

export interface DroneAirport {
    faaId?: string;
    name?: string;
}

/** `POST checkout/drone-ceiling` → `data`. */
export interface DroneCeiling {
    covered: boolean;
    controlledAirspace: boolean;
    ceilingFt?: number;
    authorizationRequired?: boolean;
    airport?: DroneAirport;
    mapEffective?: string;
    restrictions?: DroneRestriction[];
    /** Restriction sources that could not be read this time. */
    unchecked?: string[];
    /** ROBO-854: any restriction is `noFly`. */
    noFly?: boolean;
}

/**
 * Nested under one prefix (STYLE.md). Not rounded, unlike the weather: a TFR or a stadium's 3 NM edge can fall between two
 * rounded points. The date is part of the key because it decides whether a dated TFR is no-fly.
 */
export const droneQueryKeys = {
    all: ['cart-drone'] as const,
    airspace: (location: DroneLocation, shootDate: string | null) =>
        ['cart-drone', 'airspace', { ...location, shootDate }] as const
};

/**
 * One `POST checkout/drone-ceiling`. Throws on any failure (a 401, a 429, a 5xx, a body without `covered`) — the hook turns
 * every one of them into "could not check", which never blocks anything (ROBO-854 ruling 4).
 */
export async function fetchDroneCeiling(
    location: DroneLocation,
    shootDate: string | null
): Promise<DroneCeiling> {
    const response = await Api.post<Record<string, number | string>, DroneCeiling>(
        DRONE_CEILING_PATH,
        {
            latitude: location.latitude,
            longitude: location.longitude,
            ...(shootDate ? { shootDate } : {})
        }
    );
    if (!response.ok) {
        throw response.error ?? new Error(response.statusText || 'Unable to check the airspace');
    }
    const data = response.data;
    if (!data || typeof data.covered !== 'boolean')
        throw new Error('The airspace answer had no coverage');
    return data;
}
